import { UPSELLS_ENABLED } from "@/lib/features";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { track, incrementPersonProperty, setPersonProperties } from "@/lib/posthog";
import { trackBrevo, touchActivity } from "@/lib/brevo";
import { useSubscription } from "@/hooks/useSubscription";
import { useGenerationQuota, formatResetDate } from "@/hooks/useGenerationQuota";
import { startProCheckout } from "@/lib/billing";
import { PROMO } from "@/lib/promo";
import TrackOnView from "@/components/TrackOnView";
import UpsellDialog from "@/components/UpsellDialog";
import AppShell from "@/components/AppShell";
import ShareToFeedButton from "@/components/ShareToFeedButton";
import { canShareDesign } from "@/lib/feed";
import DesignResults, { type Product } from "@/components/DesignResults";
import GeneratingDesign from "@/components/GeneratingDesign";
import type { Answers } from "@/components/Questionnaire";
import type { DebugInfo, ExistingItem } from "@/pages/Index";
import { captureSentryException } from "@/lib/sentry";
import { useDebugPanel } from "@/hooks/useDebugPanel";

interface Variant {
  id: string;
  label?: string;
  image?: string;
}

interface DesignRow {
  id: string;
  user_id: string | null;
  generation_id: string | null;
  preview_url: string | null;
  ai_image_url: string | null;
  answers: any;
  total_estimate: number | null;
  created_at: string;
  products: Product[] | null;
  over_budget: boolean | null;
  debug: { variants?: Variant[] } & Record<string, any> | null;
  existing_items: ExistingItem[] | null;
  status: "pending" | "ready" | "failed";
  error_message: string | null;
  picked_variant_id: string | null;
  picked_variant_at: string | null;
  parent_design_id: string | null;
  origin: string | null;
  generation_stage?: string | null;
}


const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

const DesignDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [viewerId, setViewerId] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setViewerId(data.session?.user?.id ?? null));
  }, []);
  const location = useLocation();
  const { toast } = useToast();
  const [design, setDesign] = useState<DesignRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRetried, setAutoRetried] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [regeneratingVariantId, setRegeneratingVariantId] = useState<string | null>(null);
  const [cloningVariantId, setCloningVariantId] = useState<string | null>(null);
  const { isPro, promoEligible } = useSubscription();
  const debugPanelOpen = useDebugPanel();
  const quota = useGenerationQuota();
  const [upgrading, setUpgrading] = useState(false);

  // Guard against double-firing generation_completed / generation_failed for
  // the same design if the poll effect re-runs (tab focus, remount, etc.).
  const trackedTerminalRef = useRef<Set<string>>(new Set());
  // Time-on-view + action tracking for `results_view_exited` /
  // `past_generation_viewed`. `actionRef` is set by user actions
  // (variation click, refinement submit, explicit back). If unset when the
  // page unmounts, we assume "navigated_away".
  const mountedAtRef = useRef<number>(typeof performance !== "undefined" ? performance.now() : Date.now());
  const actionRef = useRef<"variation_selected" | "refinement_submitted" | "navigated_away" | "closed" | null>(null);
  const kickoffRef = useRef<boolean>(!!(location.state as { kickoff?: boolean } | null)?.kickoff);
  // In-memory preview passed from the onboarding flow so the "generating"
  // screen can show the original photo before Supabase storage returns the
  // public URL. Not persisted across refreshes on purpose.
  const kickoffPreview =
    (location.state as { preview?: string } | null)?.preview ?? null;

  // The `debug` column can be ~1MB per row (spatial analysis, prompts,
  // evaluations). Pulling it on every load/poll is what made past designs
  // crawl on slower connections — fetch only `debug.variants` here and load
  // the full blob lazily when the debug panel is opened.
  const fetchDesign = useCallback(async () => {
    if (!id) return;
    const { data, error } = await (supabase.from("design_history") as any)
      .select(
        "id, user_id, generation_id, preview_url, ai_image_url, answers, total_estimate, created_at, products, over_budget, debug_variants:debug->variants, existing_items, status, error_message, picked_variant_id, picked_variant_at, parent_design_id, origin, generation_stage",
      )
      .eq("id", id)
      .maybeSingle();
    if (error) {
      toast({ title: "Couldn't load design", description: error.message, variant: "destructive" });
      return null;
    }
    if (!data) {
      toast({ title: "Design not found", variant: "destructive" });
      navigate("/dashboard");
      return null;
    }
    const { debug_variants, ...rest } = data as any;
    const row = {
      ...rest,
      debug: { variants: Array.isArray(debug_variants) ? debug_variants : [] },
    } as unknown as DesignRow;
    setDesign((prev) =>
      prev && prev.id === row.id && prev.debug && Object.keys(prev.debug).length > 1
        ? { ...row, debug: prev.debug }
        : row,
    );
    return row;
  }, [id, navigate, toast]);

  // Lightweight status poll — avoids re-downloading products on every tick.
  const fetchStatus = useCallback(async () => {
    if (!id) return null;
    const { data } = await supabase
      .from("design_history")
      .select("id, status, error_message, created_at, generation_id, generation_stage")
      .eq("id", id)
      .maybeSingle();
    return (data as unknown as Pick<
      DesignRow,
      "id" | "status" | "error_message" | "created_at" | "generation_id" | "generation_stage"
    >) ?? null;
  }, [id]);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const row = await fetchDesign();
      if (!cancelled) setLoading(false);
      if (row?.status === "failed" && row.error_message) {
        toast({
          title: "Generation failed",
          description: row.error_message,
          variant: "destructive",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchDesign, toast]);

  // Fire results_view_exited (+ past_generation_viewed for previously-ready
  // designs) on unmount, using the action recorded during the session.
  useEffect(() => {
    return () => {
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      const secs = Math.max(0, Math.round((now - mountedAtRef.current) / 1000));
      const action = actionRef.current ?? "navigated_away";
      const designId = id ?? null;
      if (!designId) return;
      track("results_view_exited", {
        design_id: designId,
        time_spent_seconds: secs,
        action_taken: action,
      });
      // Only count as a "past generation" visit when this wasn't the fresh
      // kickoff navigation from the questionnaire.
      if (!kickoffRef.current) {
        track("past_generation_viewed", {
          design_id: designId,
          time_spent_seconds: secs,
        });
      }
    };
  }, [id]);

  // Watchdog: a pending row that never flips to ready/failed means the edge
  // function died before it could write a terminal state (worker killed,
  // timeout, tab closed mid-invoke). Without this the user stares at the
  // generating screen forever and `generation_failed` never fires.
  const markStalled = useCallback(
    async (row: DesignRow) => {
      if (trackedTerminalRef.current.has(row.id)) return;
      trackedTerminalRef.current.add(row.id);
      const ageMs = Date.now() - new Date(row.created_at).getTime();
      track("generation_failed", {
        design_id: row.id,
        generation_id: row.generation_id,
        error_type: "stalled_no_response",
        generation_type: "initial",
        is_regen: false,
        surface: "client_watchdog",
        age_seconds: Math.round(ageMs / 1000),
      });
      captureSentryException(new Error("Generation stalled with no terminal state"), {
        design_id: row.id,
        source: "generation-watchdog",
      });
      const message = "Generation timed out before it finished. Please try again.";
      try {
        await supabase
          .from("design_history")
          .update({ status: "failed", error_message: message })
          .eq("id", row.id)
          .eq("status", "pending");
      } catch (e) {
        console.warn("[DesignDetail] couldn't mark stalled design failed", e);
      }
      setDesign((prev) =>
        prev && prev.id === row.id ? { ...prev, status: "failed", error_message: message } : prev,
      );
    },
    [],
  );

  // Poll while pending so the generating screen swaps to results as soon
  // as the edge function marks the row ready or failed.
  useEffect(() => {
    if (!design || design.status !== "pending") return;

    const STALL_MS = 6 * 60 * 1000;
    const ageMs = Date.now() - new Date(design.created_at).getTime();
    if (ageMs > STALL_MS) {
      void markStalled(design);
      return;
    }

    const interval = setInterval(async () => {
      const row = await fetchStatus();
      if (!row) return;
      if (row.status !== "pending") await fetchDesign();
      if (row.status === "ready") {
        void quota.refresh();
        if (!trackedTerminalRef.current.has(row.id)) {
          trackedTerminalRef.current.add(row.id);
          incrementPersonProperty("total_generations", 1);
          setPersonProperties({ activated: true });
          track("generation_completed", {
            design_id: row.id,
            generation_id: row.generation_id,
            generation_type: "initial",
            is_regen: false,
          });
          // Fires once per user-initiated design (not per variant/regen).
          // Use this for session-level counts — generation_completed fires
          // ~3x per design due to variant renders and regens.
          track("design_session_completed", {
            design_id: row.id,
            generation_id: row.generation_id,
          });
        }
      } else if (row.status === "failed") {
        if (!trackedTerminalRef.current.has(row.id)) {
          trackedTerminalRef.current.add(row.id);
          track("generation_failed", {
            design_id: row.id,
            generation_id: row.generation_id,
            error_type: row.error_message || "unknown",
            generation_type: "initial",
            is_regen: false,
            surface: "row_status",
          });
        }
        toast({
          title: "Generation failed",
          description: row.error_message || "Something went wrong. Please try again.",
          variant: "destructive",
        });
      } else if (Date.now() - new Date(row.created_at).getTime() > STALL_MS) {
        void markStalled(row as unknown as DesignRow);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [design, fetchDesign, fetchStatus, toast, markStalled]);

  // Load the full debug blob only when the debug panel is actually opened.
  useEffect(() => {
    if (!debugPanelOpen || !id) return;
    if (design && design.id === id && design.debug && Object.keys(design.debug).length > 1) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("design_history")
        .select("debug")
        .eq("id", id)
        .maybeSingle();
      if (cancelled || !data) return;
      const full = (data as any).debug as Record<string, any> | null;
      if (!full) return;
      setDesign((prev) =>
        prev && prev.id === id
          ? { ...prev, debug: { ...full, variants: full.variants ?? prev.debug?.variants ?? [] } }
          : prev,
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [debugPanelOpen, id, design]);

  // Silently re-run product matching when a saved design has no picks yet.
  useEffect(() => {
    if (!design || autoRetried) return;
    if (design.status !== "ready") return;
    if (Array.isArray(design.products) && design.products.length > 0) return;
    if (!design.ai_image_url) return;
    setAutoRetried(true);
    setRetrying(true);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("match-design-products", {
          body: {
            aiImage: design.ai_image_url,
            generationId: design.generation_id,
            designId: design.id,
            answers: design.answers,
          },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        setDesign((prev) =>
          prev
            ? {
                ...prev,
                products: Array.isArray(data?.products) ? data.products : prev.products,
                total_estimate: data?.totalEstimate ?? prev.total_estimate,
                over_budget: typeof data?.overBudget === "boolean" ? data.overBudget : prev.over_budget,
              }
            : prev,
        );
      } catch (e) {
        console.warn("[DesignDetail] auto product-match retry failed", e);
      } finally {
        setRetrying(false);
      }
    })();
  }, [design, autoRetried]);

  // Build the variant list. Prefer images stored on debug.variants; fall back
  // to reconstructing the storage URL using the predictable per-variant path
  // (`{user_id}/{generation_id}/ai-{variantId}.jpg`); finally, fall back to
  // the single primary ai_image_url for legacy rows.
  const variations = useMemo(() => {
    if (!design) return [] as { id: string; label: string; image: string }[];
    const rawVariants = design.debug?.variants ?? [];
    const userId = design.user_id;
    const generationId = design.generation_id;
    const reconstructUrl = (variantId: string): string | null => {
      if (!userId || !generationId || !SUPABASE_URL) return null;
      return `${SUPABASE_URL}/storage/v1/object/public/design-images/${userId}/${generationId}/ai-${variantId}.jpg`;
    };
    const enriched = rawVariants
      .map((v, i) => ({
        id: v?.id ?? `design-${i + 1}`,
        label: v?.label ?? `Design ${i + 1}`,
        image: v?.image ?? (v?.id ? reconstructUrl(v.id) : null) ?? undefined,
      }))
      .filter((v) => !!v.image) as { id: string; label: string; image: string }[];
    if (enriched.length > 0) return enriched;
    if (design.ai_image_url) return [{ id: "design-1", label: "Design 1", image: design.ai_image_url }];
    return [];
  }, [design]);

  const hasProducts = !!(design?.products && design.products.length > 0);

  const handleRegenerateVariant = async (
    variantId: string,
    opts?: { existingItems?: ExistingItem[] },
  ) => {
    if (!design || !design.generation_id || !design.preview_url || regeneratingVariantId) return;
    track("variant_regenerated", {
      generation_id: design.generation_id,
      variant_id: variantId,
      with_existing_overrides: !!opts?.existingItems,
    });
    const genRequestedProps = {
      design_id: design.id,
      generation_id: design.generation_id,
      variant_id: variantId,
      generation_type: "regen" as const,
      is_regen: true,
      source: "variant_regen",
    };
    console.debug("[track] generation_requested", genRequestedProps);
    track("generation_requested", genRequestedProps);
    void trackBrevo("image_generated", {
      design_id: design.id,
      generation_id: design.generation_id,
      generation_type: "regen",
    });
    touchActivity();
    setRegeneratingVariantId(variantId);
    try {
      const answersForCall = opts?.existingItems
        ? {
            ...(design.answers as Answers),
            existingItems: opts.existingItems.map((p) => ({
              name: p.name,
              decision: p.decision,
            })),
          }
        : (design.answers as Answers);
      const { data, error } = await supabase.functions.invoke("generate-room-design", {
        body: {
          image: design.preview_url,
          answers: answersForCall,
          regenerateVariantId: variantId,
          generationId: design.generation_id,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.image) throw new Error("No image returned");

      // Cache-bust: the variant uploads to a stable storage path with upsert,
      // so the public URL is identical after regen. Append ?t= so the browser
      // refetches and React re-renders with a new src string.
      const bustedImage = `${data.image}${data.image.includes("?") ? "&" : "?"}t=${Date.now()}`;

      setDesign((prev) => {
        if (!prev) return prev;
        const prevDebug = (prev.debug ?? {}) as any;
        const prevVariants: Variant[] = Array.isArray(prevDebug.variants) ? prevDebug.variants : [];
        const regenPlan = data.debug?.variants?.[0];
        const nextVariants: Variant[] = (() => {
          const map = new Map(prevVariants.map((v) => [v.id, { ...v }] as const));
          const existing = map.get(variantId) ?? { id: variantId };
          map.set(variantId, {
            ...existing,
            ...(regenPlan ?? {}),
            id: variantId,
            image: bustedImage,
          });
          return Array.from(map.values());
        })();
        return {
          ...prev,
          ai_image_url: prev.debug?.variants?.[0]?.id === variantId ? bustedImage : prev.ai_image_url,
          existing_items: Array.isArray(data.existingItems)
            ? (data.existingItems as ExistingItem[])
            : opts?.existingItems ?? prev.existing_items,
          debug: {
            ...prevDebug,
            step1: data.debug?.step1 ?? prevDebug.step1,
            step2: data.debug?.step2 ?? prevDebug.step2,
            anchors: data.debug?.anchors ?? prevDebug.anchors,
            variants: nextVariants,
          },
        };
      });
      track("generation_completed", {
        design_id: design.id,
        generation_id: design.generation_id,
        variant_id: variantId,
        generation_type: "regen",
        is_regen: true,
      });
    } catch (err: any) {
      console.error("Variant regeneration failed:", err);
      captureSentryException(err, { design_id: design?.id, source: "variant-regen" });
      track("generation_failed", {
        design_id: design.id,
        generation_id: design.generation_id,
        error_type: err?.message || "unknown",
        generation_type: "regen",
        is_regen: true,
        variant_id: variantId,
      });
      toast({
        title: "Regeneration failed",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRegeneratingVariantId(null);
    }
  };





  const handleCloneVariation = useCallback(async (variantId: string) => {
    if (!design || cloningVariantId) return;
    const variant = variations.find((v) => v.id === variantId);
    if (!variant || !design.user_id) return;
    setCloningVariantId(variantId);
    try {
      const newGenerationId = (globalThis.crypto?.randomUUID?.() ?? `gen_${Date.now()}`);
      const insertRow: any = {
        user_id: design.user_id,
        generation_id: newGenerationId,
        preview_url: variant.image,
        ai_image_url: variant.image,
        answers: design.answers,
        total_estimate: design.total_estimate,
        products: design.products as any,
        over_budget: design.over_budget,
        existing_items: design.existing_items as any,
        debug: { variants: [{ id: variant.id, label: variant.label, image: variant.image }] },
        status: "ready",
        picked_variant_id: variant.id,
        picked_variant_at: new Date().toISOString(),
        parent_design_id: design.id,
        origin: "variation_clone",
      };
      const { data, error } = await supabase
        .from("design_history")
        .insert(insertRow)
        .select("id")
        .single();
      if (error) throw error;
      track("variation_cloned_to_new_project", {
        source_design_id: design.id,
        source_variant_id: variantId,
        new_design_id: data.id,
      });
      actionRef.current = "variation_selected";
      navigate(`/dashboard/design/${data.id}`);
    } catch (err: any) {
      toast({ title: "Couldn't open variation", description: err?.message || "Please try again.", variant: "destructive" });
      setCloningVariantId(null);
    }
  }, [cloningVariantId, design, navigate, toast, variations]);

  const previewForGenerating = design?.preview_url ?? kickoffPreview ?? null;


  if (!loading && design && design.status === "pending") {
    return (
      <AppShell>
        <main className="relative flex-1 w-full min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-8">
          <Link
            to="/dashboard"
            onClick={() => { actionRef.current = "closed"; }}
            className="absolute top-6 left-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to your space
          </Link>
          <GeneratingDesign preview={previewForGenerating} stage={design?.generation_stage ?? null} />
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <main className="flex-1 w-full max-w-5xl mx-auto px-5 py-8">
        <Link
          to="/dashboard"
          onClick={() => { actionRef.current = "closed"; }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to your space
        </Link>

        <UpsellDialog type="design_limit" active={design?.status === "ready" && !isPro && !quota.loading && !quota.unlimited && quota.remaining <= 0} promoEligible={promoEligible} />
        {UPSELLS_ENABLED && !isPro && !quota.loading && !quota.unlimited && quota.remaining <= 0 && (
          <TrackOnView
            event="upsell_viewed"
            properties={{ placement: "render_banner", promo_eligible: promoEligible, quota_remaining: quota.remaining, plan: "free" }}
            className="mb-6 block"
          >
          <div className="w-full bg-card border border-border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
              <div className="shrink-0 w-8 h-8 rounded-full bg-gradient-cta flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  Monthly limit reached · Get 5x more designs with Huelo Pro
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {promoEligible ? (
                    <>
                      <span className="font-semibold text-primary">{PROMO.badge}:</span>{" "}
                      <span className="font-semibold text-foreground">${PROMO.price}</span>{" "}
                      <span className="line-through">${PROMO.regularPrice}</span> your first month, then ${PROMO.regularPrice}/month.
                    </>
                  ) : (
                    <>${PROMO.regularPrice}/month, cancel anytime.</>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={async () => {
                track("upsell_clicked", { placement: "render_banner", promo_eligible: promoEligible, quota_remaining: quota.remaining, plan: "free" });
                setUpgrading(true);
                try { window.open(await startProCheckout("month", "pro", "render_banner"), "_blank"); }
                catch { navigate("/pricing?plan=pro&billing=monthly"); }
                finally { setUpgrading(false); }
              }}
              disabled={upgrading}
              className="shrink-0 self-start sm:self-auto inline-flex items-center gap-1.5 rounded-full bg-gradient-cta text-primary-foreground px-4 py-2 text-xs font-semibold hover:opacity-95 transition-opacity disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none"
            >
              {upgrading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {promoEligible ? PROMO.cta : "Upgrade to Pro"}
            </button>
          </div>
          </TrackOnView>
        )}

        {loading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : !design ? null : design.status === "failed" ? (
          <div className="max-w-lg mx-auto text-center space-y-4 py-16">
            <h1 className="text-2xl md:text-4xl font-display text-foreground">Generation failed</h1>
            <p className="text-sm text-muted-foreground">
              {design.error_message || "Something went wrong. Please try again."}
            </p>
            <button
              onClick={() => navigate("/app")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold tracking-normal bg-foreground text-background hover:opacity-90 transition-opacity"
            >
              Start a new design
            </button>
          </div>
        ) : !design.ai_image_url || !design.preview_url ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading design…
          </div>
        ) : (
          <>
          {canShareDesign(design, viewerId) && (
            <div className="mb-4 flex sm:justify-end">
              <ShareToFeedButton designId={design.id} imageUrl={design.ai_image_url} style={design.answers?.style} />
            </div>
          )}
          <DesignResults
            preview={design.preview_url}
            aiImage={design.ai_image_url}
            variations={variations}
            answers={design.answers}
            products={design.products}
            overBudget={design.over_budget ?? undefined}
            totalEstimate={design.total_estimate ?? undefined}
            designId={design.id}
            generationId={design.generation_id}
            regeneratingVariantId={regeneratingVariantId}
            existingItems={design.existing_items ?? []}
            debug={design.debug as DebugInfo | null}
            loadingProducts={retrying && !hasProducts}
            pickedVariantId={design.picked_variant_id ?? variations[0]?.id ?? ""}
            isPro={isPro}
            
            onCloneVariation={handleCloneVariation}
            cloningVariantId={cloningVariantId}
            onReset={() => navigate("/app")}
            onRegenerateVariant={handleRegenerateVariant}
            onUserAction={(a) => { actionRef.current = a; }}
          />
          </>
        )}

      </main>
    </AppShell>
  );
};

export default DesignDetail;
